import "./AgentScoringRulesRoute.less";

import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
    GuxButton,
    GuxRadialLoading,
    GuxTooltip,
} from "genesys-spark-components-react";
import type { AgentScoringRule } from "@genesys/cloud-frontend/models";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    selectCanAddScoringRule,
    selectCanDeleteScoringRule,
    selectCanViewScoringRule,
} from "features/auth/currentUserSlice";
import { useStrings } from "features/i18n/useStrings";
import tableStrings from "features/agentScoringRules/AgentScoringRulesTable.strings";
import commonStrings from "features/agentScoringRules/agentScoringRulesCommon.strings";
import AgentScoringRulesTable from "features/agentScoringRules/AgentScoringRulesTable";
import DeleteRuleConfirmation from "features/agentScoringRules/DeleteRuleConfirmation";
import {
    fetchAgentScoringRules,
    selectAgentScoringRulesCount,
    selectAgentScoringRulesLoadStatus,
    selectIsAgentScoringRulesLoading,
} from "features/agentScoringRules/agentScoringRulesSlice";
import agentScoringRulesToasts from "features/agentScoringRules/agentScoringRulesToasts";
import {
    loadAgentScoringRulesMaxOuterLimit,
    selectAgentScoringRulesMaxOuterLimit,
} from "features/qualityForm/outerLimitSlice";
import { toGuxDisabled } from "util/componentUtil";
import { useAgentScoringRuleNavigation } from "features/agentScoringRules/useAgentScoringRuleNavigation";
import { NotFound } from "./NotFound";

/**
 * Rules list permission behaviour:
 * - view: required to load rules; without it the table shows an access-restricted
 *   empty state and a one-shot toast (same pattern as EvaluationRoute)
 * - add: disables New Rule (also disabled without view) and sets the matching tooltip
 * - edit/delete: enforced in the table (disable + tooltips; actions no-op without permission)
 * - delete confirm: only opened when the table emits delete (requires delete permission)
 */
export function AgentScoringRulesRoute() {
    const { programId } = useParams();
    const { openCreate, openEditor } = useAgentScoringRuleNavigation(
        programId ?? ""
    );
    const dispatch = useAppDispatch();
    const t = useStrings(tableStrings);
    const common = useStrings(commonStrings);

    const canView = useAppSelector(selectCanViewScoringRule);
    const canAdd = useAppSelector(selectCanAddScoringRule);
    const canDelete = useAppSelector(selectCanDeleteScoringRule);
    const isLoading = useAppSelector(selectIsAgentScoringRulesLoading);
    const loadStatus = useAppSelector(selectAgentScoringRulesLoadStatus);
    const rulesCount = useAppSelector(selectAgentScoringRulesCount);
    const maxRules = useAppSelector(selectAgentScoringRulesMaxOuterLimit);

    const [ruleToDelete, setRuleToDelete] = useState<AgentScoringRule | null>(
        null
    );
    // Same one-shot toast pattern as EvaluationRoute / EvaluationForm for
    // blocked access (avoid re-firing on every render).
    const [hasShownAccessDeniedToast, setHasShownAccessDeniedToast] =
        useState(false);
    const [hasShownLoadFailureToast, setHasShownLoadFailureToast] =
        useState(false);

    useEffect(() => {
        if (programId && canView) {
            dispatch(fetchAgentScoringRules(programId));
            dispatch(loadAgentScoringRulesMaxOuterLimit());
        }
    }, [dispatch, programId, canView]);

    useEffect(() => {
        if (!canView && !hasShownAccessDeniedToast) {
            agentScoringRulesToasts.accessDenied();
            setHasShownAccessDeniedToast(true);
        }
    }, [canView, hasShownAccessDeniedToast]);

    useEffect(() => {
        if (canView && loadStatus === "failed" && !hasShownLoadFailureToast) {
            agentScoringRulesToasts.listLoadFailure();
            setHasShownLoadFailureToast(true);
        }
        if (loadStatus === "succeeded" || loadStatus === "loading") {
            setHasShownLoadFailureToast(false);
        }
    }, [canView, loadStatus, hasShownLoadFailureToast]);

    if (!programId) {
        return <NotFound />;
    }

    const atLimit = maxRules != null && rulesCount >= maxRules;
    // Tooltip priority: no view → no add → at max → default.
    const addRuleTooltip = !canView
        ? common.labels.accessRestricted
        : !canAdd
        ? t.labels.noAddPermissionTooltip
        : atLimit && maxRules != null
        ? t.labels.maxRulesReachedTooltip({ count: maxRules })
        : t.labels.newRuleTooltip;

    // New Rule is disabled without view/add permission or when at max rules.
    const isAddRuleDisabled = !canView || !canAdd || atLimit;
    const addRuleDisabled = toGuxDisabled(isAddRuleDisabled);
    const loadFailed = loadStatus === "failed";

    const handleAddRule = () => {
        if (isAddRuleDisabled) return;
        openCreate();
    };

    const handleDeleteRule = (rule: AgentScoringRule) => {
        if (!canDelete) return;
        setRuleToDelete(rule);
    };

    return (
        <div className="AgentScoringRulesRoute">
            <div className="AgentScoringRulesRoute-header">
                <h3 className="gux-heading-subheading AgentScoringRulesRoute-title">
                    {common.labels.title}
                </h3>
                <p className="AgentScoringRulesRoute-description">
                    {t.labels.description}
                </p>
                {maxRules != null && (
                    <p className="AgentScoringRulesRoute-note">
                        <strong>{t.labels.noteLabel}</strong>{" "}
                        {t.labels.maxRulesNote({ count: maxRules })}
                    </p>
                )}
            </div>

            <div className="AgentScoringRulesRoute-body">
                <div className="AgentScoringRulesRoute-toolbar">
                    <div className="AgentScoringRulesRoute-newRuleButton">
                        <GuxButton
                            accent="primary"
                            disabled={addRuleDisabled}
                            aria-disabled={addRuleDisabled}
                            onClick={handleAddRule}
                            data-testid="add-agent-scoring-rule"
                        >
                            {t.labels.newRule}
                        </GuxButton>
                        <GuxTooltip>{addRuleTooltip}</GuxTooltip>
                    </div>
                </div>

                {isLoading ? (
                    <div className="AgentScoringRulesRoute-loading">
                        <GuxRadialLoading screenreaderText={t.labels.loading} />
                    </div>
                ) : (
                    <div className="AgentScoringRulesRoute-table">
                        <AgentScoringRulesTable
                            programId={programId}
                            loadFailed={loadFailed}
                            onEditRule={(ruleId) => openEditor(ruleId)}
                            onDeleteRule={handleDeleteRule}
                        />
                    </div>
                )}
            </div>

            {ruleToDelete && (
                <DeleteRuleConfirmation
                    programId={programId}
                    rule={ruleToDelete}
                    onClose={() => setRuleToDelete(null)}
                />
            )}
        </div>
    );
}

export default AgentScoringRulesRoute;
